import React, { useEffect, useRef, useState } from "react";
import { project_name } from "../Config/config";
import { path_url, responseMessages } from "../Config/config";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import "../Style/ApplyNowForm.css";

export default function ApplyNowForm({ jobTitle, onClose, onSubmit }) {
  const topSectionRef = useRef(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    if (topSectionRef.current) {
      topSectionRef.current.classList.add("highlight-section");
      const timer = setTimeout(() => {
        if (topSectionRef.current) {
          topSectionRef.current.classList.remove("highlight-section");
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    position: jobTitle || "",
    experience: "",
    currentCompany: "",
    resume: null,
    coverLetter: "",
    portfolio: "",
    workType: "",
    linkedin: "",
    availableFrom: "",
    hearAbout: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file") {
      const file = files[0];
      if (file && file.size > 5 * 1024 * 1024) {
        setErrors({ ...errors, resume: "File size should be less than 5MB" });
        return;
      }
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (file && !allowedTypes.includes(file.type)) {
        setErrors({
          ...errors,
          resume: "Only PDF, DOC, and DOCX files are allowed",
        });
        return;
      }
      setFormData({ ...formData, [name]: file });
      if (errors.resume) {
        setErrors({ ...errors, resume: "" });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = "Full name is required";
    if (!formData.workType.trim()) {
      newErrors.workType = "Work Type is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ""))) {
      newErrors.phone = "Phone number must be 10 digits";
    }
    if (!formData.resume) newErrors.resume = "Resume is required";
    if (!formData.experience)
      newErrors.experience = "Please select experience level";

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(0);

    try {
      const submitData = new FormData();

      const applicationData = {
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        position: formData.position,
        experience: formData.experience,
        currentCompany: formData.currentCompany || "",
        coverLetter: formData.coverLetter || "",
        portfolio: formData.portfolio || "",
        workType: formData.workType,
        linkedin: formData.linkedin || "",
        availableFrom: formData.availableFrom || "",
        hearAbout: formData.hearAbout || "",
      };

      submitData.append("applicationData", JSON.stringify(applicationData));

      if (formData.resume) {
        submitData.append("resume", formData.resume);
      }

      const response = await fetch(`${path_url}/career/applynow`, {
        method: "POST",
        body: submitData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        toast.error("Internal spplication error, please contact support team");
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`,
        );
      }

      const result = await response.json();

      setSubmitStatus("success");
      toast.success("Application Submitted Successfully");
      if (onSubmit) {
        onSubmit(formData);
      }

      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error("Submission error:", error);
      setSubmitStatus("error");
      setErrors({
        submit:
          error.message || "Failed to submit application. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
      setUploadProgress(100);
    }
  };

  return (
    <>
      <div className="anf-overlay" onClick={onClose}>
        <div className="anf-modal" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="anf-header">
            <h3>Apply for {jobTitle || "Position"}</h3>
            <button className="anf-close-btn" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="anf-form">
            {submitStatus === "success" && (
              <div className="anf-status anf-status-success">
                ✓ Application submitted successfully! We'll contact you soon.
              </div>
            )}

            {submitStatus === "error" && (
              <div className="anf-status anf-status-error">
                ✗ {errors.submit || "Something went wrong. Please try again."}
              </div>
            )}

            {isSubmitting && (
              <div className="anf-progress-wrap">
                <div className="anf-progress-track">
                  <div
                    className="anf-progress-fill"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <small className="anf-progress-label">
                  Submitting application... {uploadProgress}%
                </small>
              </div>
            )}

            <div className="row anf-row">
              {/* Full Name */}
              <div className="col-md-6 anf-field">
                <label className="anf-label">
                  Full Name <span className="anf-required">*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  className={`form-control ${errors.fullName ? "anf-invalid" : ""}`}
                />
                {errors.fullName && (
                  <small className="anf-error-text">{errors.fullName}</small>
                )}
              </div>

              {/* Email */}
              <div className="col-md-6 anf-field">
                <label className="anf-label">
                  Email Address <span className="anf-required">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`form-control ${errors.email ? "anf-invalid" : ""}`}
                />
                {errors.email && (
                  <small className="anf-error-text">{errors.email}</small>
                )}
              </div>

              {/* Phone */}
              <div className="col-md-6 anf-field">
                <label className="anf-label">
                  Phone Number <span className="anf-required">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`form-control ${errors.phone ? "anf-invalid" : ""}`}
                  placeholder="10-digit mobile number"
                />
                {errors.phone && (
                  <small className="anf-error-text">{errors.phone}</small>
                )}
              </div>

              {/* Experience */}
              <div className="col-md-6 anf-field">
                <label className="anf-label">
                  Years of Experience <span className="anf-required">*</span>
                </label>
                <select
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  className={`anf-select ${errors.experience ? "anf-invalid" : ""}`}
                >
                  <option value="">Select experience</option>
                  <option value="Fresher">Fresher (0 Years)</option>
                  <option value="0-1">0–1 Years</option>
                  <option value="1-2">1–2 Years</option>
                  <option value="2-3">2–3 Years</option>
                  <option value="3-5">3–5 Years</option>
                  <option value="5+">5+ Years</option>
                </select>
                {errors.experience && (
                  <small className="anf-error-text">{errors.experience}</small>
                )}
              </div>

              {/* Preferred Work */}
              <div className="col-md-6 anf-field">
                <label className="anf-label">
                  Preferred Work <span className="anf-required">*</span>
                </label>
                <select
                  name="workType"
                  value={formData.workType}
                  onChange={handleChange}
                  className={`anf-select ${errors.workType ? "anf-invalid" : ""}`}
                >
                  <option value="">Select work preference</option>
                  <option value="Onsite">Onsite</option>
                  <option value="Remote">Remote</option>
                  <option value="Hybrid">Hybrid</option>
                </select>
                {errors.workType && (
                  <small className="anf-error-text">{errors.workType}</small>
                )}
              </div>

              {/* Position (Auto-filled, disabled) */}
              <div className="col-md-6 anf-field">
                <label className="anf-label">Applying For</label>
                <input
                  type="text"
                  name="position"
                  value={formData.position}
                  disabled
                  className="form-control anf-field-disabled"
                />
              </div>

              {/* Current Company */}
              <div className="col-md-6 anf-field">
                <label className="anf-label">Current Company (if any)</label>
                <input
                  type="text"
                  name="currentCompany"
                  value={formData.currentCompany}
                  onChange={handleChange}
                  className="form-control"
                />
              </div>

              {/* Available From */}
              <div className="col-md-6 anf-field">
                <label className="anf-label">Available to join from</label>
                <input
                  type="text"
                  name="availableFrom"
                  value={formData.availableFrom}
                  onChange={handleChange}
                  placeholder="Immediate / 15 days / 30 days"
                  className="form-control"
                />
              </div>

              {/* LinkedIn Profile */}
              <div className="col-md-6 anf-field">
                <label className="anf-label">LinkedIn Profile URL</label>
                <input
                  type="url"
                  name="linkedin"
                  value={formData.linkedin}
                  onChange={handleChange}
                  className="form-control"
                />
              </div>

              {/* Portfolio/GitHub */}
              <div className="col-md-6 anf-field">
                <label className="anf-label">Portfolio/GitHub URL</label>
                <input
                  type="url"
                  name="portfolio"
                  value={formData.portfolio}
                  onChange={handleChange}
                  className="form-control"
                />
              </div>

              {/* How did you hear about us */}
              <div className="col-md-12 anf-field">
                <label className="anf-label">How did you hear about us?</label>
                <select
                  name="hearAbout"
                  value={formData.hearAbout}
                  onChange={handleChange}
                  className="anf-select"
                >
                  <option value="">Select an option</option>
                  <option value="LinkedIn">LinkedIn</option>
                  <option value="Indeed">Indeed</option>
                  <option value="Naukri">Naukri.com</option>
                  <option value="Company Website">Company Website</option>
                  <option value="Referral">Employee Referral</option>
                  <option value="Social Media">Social Media</option>
                  <option value="College/University">College/University</option>
                  <option value="Google Search">Google Search</option>
                  <option value="Job Fair">Job Fair</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Resume Upload */}
              <div className="col-md-12 anf-field">
                <label className="anf-label">
                  Upload Resume <span className="anf-required">*</span>
                </label>
                <input
                  type="file"
                  name="resume"
                  onChange={handleChange}
                  accept=".pdf,.doc,.docx"
                  className={`anf-file-input ${errors.resume ? "anf-invalid" : ""}`}
                />
                {errors.resume && (
                  <small className="anf-error-text">{errors.resume}</small>
                )}
                <small className="anf-hint-text">
                  Allowed formats: PDF, DOC, DOCX (Max 5MB)
                </small>
              </div>

              {/* Cover Letter */}
              <div className="col-md-12 anf-field">
                <label className="anf-label">Cover Letter (Optional)</label>
                <textarea
                  name="coverLetter"
                  value={formData.coverLetter}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Tell us why you're interested in this position and what makes you a great fit..."
                  className="anf-textarea"
                />
              </div>
            </div>

            {/* Form Actions */}
            <div className="anf-actions">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="anf-btn anf-btn-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="anf-btn anf-btn-submit"
              >
                {isSubmitting ? "Submitting..." : "Submit Application"}
              </button>
            </div>
          </form>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </>
  );
}